import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from 'react';
import { useParams } from 'react-router';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Customer } from '../../../types';
import { useCustomerService } from '../../customer/services/customerService';

const STORAGE_KEY_LAST_CUSTOMER = 'strands.lastCustomerId';

export const customerKeys = {
  detail: (customerId: string | undefined) => ['customer', customerId] as const,
};

interface InvestigationData {
  selectedCustomer: Customer | null;
  customers: Customer[];
  lastCustomerId: string | null;
  isCustomerError: boolean;
}

const InvestigationDataContext = createContext<InvestigationData | undefined>(undefined);

export const useInvestigationData = () => {
  const context = useContext(InvestigationDataContext);
  if (context === undefined) {
    throw new Error('useInvestigationData must be used within InvestigationProvider');
  }
  return context;
};

interface InvestigationActions {
  loadCustomers: () => Promise<void>;
  refreshCustomer: () => void;
}

const InvestigationActionsContext = createContext<InvestigationActions | undefined>(undefined);

export const useInvestigationActions = () => {
  const context = useContext(InvestigationActionsContext);
  if (context === undefined) {
    throw new Error('useInvestigationActions must be used within InvestigationProvider');
  }
  return context;
};

export const useInvestigation = () => {
  const data = useInvestigationData();
  const actions = useInvestigationActions();
  return { ...data, ...actions };
};

interface InvestigationProviderProps {
  children: ReactNode;
}

export const InvestigationProvider: React.FC<InvestigationProviderProps> = ({ children }) => {
  // The URL route param is the single source of truth for the active customer.
  // The customer object is derived from it via a cached query — never written
  // imperatively — so navigation is the only way to change customer and no local
  // state can drift from the URL.
  const { customerId } = useParams<{ customerId: string }>();
  const customerService = useCustomerService();
  const queryClient = useQueryClient();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [lastCustomerId, setLastCustomerId] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY_LAST_CUSTOMER);
  });

  const customerQuery = useQuery({
    queryKey: customerKeys.detail(customerId),
    queryFn: () => customerService.getCustomerById(customerId!),
    enabled: !!customerId,
    // Keep the previous customer rendered while a switch to a not-yet-cached
    // customer loads, instead of blanking the dashboard to a spinner.
    placeholderData: keepPreviousData,
  });
  const selectedCustomer = customerQuery.data ?? null;

  // Remember the last customer actually loaded, for the landing-page restore.
  useEffect(() => {
    if (customerId && customerQuery.isSuccess) {
      localStorage.setItem(STORAGE_KEY_LAST_CUSTOMER, customerId);
      setLastCustomerId(customerId);
    }
  }, [customerId, customerQuery.isSuccess]);

  const loadCustomers = useCallback(async () => {
    try {
      const customersData = await customerService.getCustomers();
      setCustomers(customersData);
    } catch (error) {
      console.error('Failed to load customers:', error);
      throw error;
    }
  }, [customerService]);

  const refreshCustomer = useCallback(() => {
    if (customerId) {
      queryClient.invalidateQueries({ queryKey: customerKeys.detail(customerId) });
    }
  }, [customerId, queryClient]);

  const dataValue = useMemo<InvestigationData>(() => ({
    selectedCustomer,
    customers,
    lastCustomerId,
    isCustomerError: customerQuery.isError,
  }), [selectedCustomer, customers, lastCustomerId, customerQuery.isError]);

  const actionsValue = useMemo<InvestigationActions>(() => ({
    loadCustomers,
    refreshCustomer,
  }), [loadCustomers, refreshCustomer]);

  return (
    <InvestigationDataContext.Provider value={dataValue}>
      <InvestigationActionsContext.Provider value={actionsValue}>
        {children}
      </InvestigationActionsContext.Provider>
    </InvestigationDataContext.Provider>
  );
};
